import { useEffect, useMemo, useRef, useState } from 'react';

import { FormikProps, FormikProvider, useFormik } from 'formik';
import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/Auth';
import { useModalContext } from '../../context/Modal';
import { useUserStrategies } from '../../context/UserStrategies';

import { Icon, Loading } from '../UI';
import { ChooseConnectionAndAssetCategory } from './ChooseConnectionAndAssetCategory';
import { AddCustomAssetOrCreateStrategyAssetCategory } from './CustomAsset';
import { FooterNavigator } from '../Footer/FooterNavigator';
import { BankConnect } from './BankAsset/BankConnect';

import {
  ADD_NFT_TOKEN,
  ADD_PROPERTY_ESTIMATION,
  CREATE_STRATEGY_ASSET_CATEGORY,
  CREATE_STRATEGY_ASSETS,
  GET_INSTITUTIONS_LIST,
  GET_PLAID_LINK_TOKEN,
  LIST_STRATEGY_ASSET_CATEGORIES_BY_PROFILE,
  LIST_STRATEGY_NFT_COLLECTIONS,
  LIST_WALLETS,
  UPDATE_STRATEGY_ASSETS,
} from '../../http/apollo-queries';

import { CreateStrategyAssetCategory } from '../NewStrategy/types';
import {
  BankCategory,
  CreateStrategyAssetInput,
  FormValues,
  UpdateStrategyAssetInput,
  UpsertStrategyAssetProps,
} from './types';
import PropertyAssetForm from './PropertyAsset/PropertyAssetForm';
import NFTAssetForm from './NFTAsset/NFTAssetForm';
import { UpgradeSubscription } from '../UpgradeSubscription/UpgradeSubscription';
import { logger } from '../../helpers/logger';
import { useNewStrategyContext } from '../NewStrategy/NewStrategyContext';
import {
  ConnectionTypeEnum,
  CreateStrategyAssetStepEnum,
  CurrencyCodeEnum,
  MembershipPlanSkuEnum,
  StrategyAssetCategoryTypeEnum,
} from '../../constants/enums';
import {
  prepareManualCreateInput,
  prepareManualEditInput,
} from './helpers/prepareManualInput';
import { prepareWalletInput } from './helpers/prepareWalletlInput';
import { prepareBankInput } from './helpers/prepareBanklInput';
import { FinancialAssetForm } from './FinancialAsset';
import { WalletAssets } from './WalletAsset/WalletAssets';
import { BankAssets } from './BankAsset/BankAssets';
import { Debug } from '../Debug/Debug';
import { WalletConnect } from './WalletAsset/WalletConnect';
import { formatAWSDate } from '../../helpers/functions';
import { getCreateStrategyAssetStep } from './helpers/getCreateStrategyAssetStep';

export const UpsertAsset = ({
  assetCategory,
  selectedConnectionType = ConnectionTypeEnum.MANUAL,
  asset,
  strategyId = '',
  walletAccount,
  onCloseClick = () => {},
  skipStrategy,
  strategyAsset,
  action = 'CREATE',
}: UpsertStrategyAssetProps) => {
  const { t } = useTranslation();

  const { user } = useAuth();

  const { closeModal, setTitle: setModalTitle } = useModalContext();

  const { isTooltipIconVisible } = useNewStrategyContext();
  const {
    allStrategies,
    strategies,
    loading: loadingStrategies,
    refetch: refetchStrategies,
  } = useUserStrategies();

  const [connectionType, setConnectionType] = useState<ConnectionTypeEnum>(
    selectedConnectionType
  );

  const [createStrategyAssetStep, setCreateStrategyAssetStep] =
    useState<CreateStrategyAssetStepEnum>(
      action === 'EDIT' || action === 'ADD_TO_STRATEGY'
        ? getCreateStrategyAssetStep(selectedConnectionType, assetCategory).step
        : CreateStrategyAssetStepEnum.INITIAL
    );

  const [isContinueButtonLoading, setIsContinueButtonLoading] = useState(false);
  const [assetCategorySelection, setAssetCategorySelection] = useState<
    CreateStrategyAssetCategory | undefined
  >(assetCategory);

  // Common API
  const {
    data: {
      listStrategyAssetCategories: { items: strategyAssetCategories = [] } = {},
    } = {},
    loading: loadingCategories,
    refetch: refetchCategories,
  } = useQuery<{
    listStrategyAssetCategories: {
      items: CreateStrategyAssetCategory[];
    };
  }>(LIST_STRATEGY_ASSET_CATEGORIES_BY_PROFILE, {
    variables: { profileId: user?.profile?.id },
  });

  const [createStrategyAsset, { loading: createStrategyAssetLoading }] =
    useMutation(CREATE_STRATEGY_ASSETS);

  const [updateStrategyAsset, { loading: updateStrategyAssetLoading }] =
    useMutation(UPDATE_STRATEGY_ASSETS);

  const [createStrategyAssetCategory, { loading: loadingCreateCategory }] =
    useMutation(CREATE_STRATEGY_ASSET_CATEGORY, {
      onCompleted: () => refetchCategories(),
    });

  // Connect Bank API
  const {
    data: { getInstitutionsList: bankCategories = [] } = {},
    loading: loadingInstitutionsList,
    refetch: refetchBankCategories,
  } = useQuery<{
    getInstitutionsList: BankCategory[];
  }>(GET_INSTITUTIONS_LIST, {
    variables: { profileId: user?.profile?.id },
    onCompleted: (data) => logger.log('AddAsset:getInstitutionsList', data),
    onError: (err) => logger.error(err),
  });

  const { data: { getLinkToken: token } = {}, refetch: getNewPlaidLinkToken } =
    useQuery(GET_PLAID_LINK_TOKEN, {
      fetchPolicy: 'no-cache',
    });

  // Property API
  const [addPropertyEstimation] = useMutation(ADD_PROPERTY_ESTIMATION);

  // NFT API
  const [addNftToken] = useMutation(ADD_NFT_TOKEN, {
    refetchQueries: [
      {
        query: LIST_STRATEGY_NFT_COLLECTIONS,
        variables: {
          profileId: user?.profile?.id,
          strategyId,
        },
      },
    ],
  });

  // Connect Wallet API
  const { data: { listWallets = [] } = {}, loading: loadingWallets } =
    useQuery<{
      listWallets: {
        address: string;
        id: string;
        logo: string;
        name: string;
      }[];
    }>(LIST_WALLETS, {
      variables: { profileId: user?.profile?.id },
      onCompleted: (data) => logger.log('AddAsset:listWallets', data),
      onError: (err) => logger.error(err),
    });

  const walletAccountRef = useRef(walletAccount);

  const categories: CreateStrategyAssetCategory[] = (
    connectionType === ConnectionTypeEnum.MANUAL
      ? strategyAssetCategories.map((assetCategory) => ({
          ...assetCategory,
        }))
      : connectionType === ConnectionTypeEnum.BANK
      ? bankCategories.map(
          ({ id, bankName: title }) =>
            ({
              id,
              title,
              key: title,
              type: StrategyAssetCategoryTypeEnum.SYSTEM,
            } as CreateStrategyAssetCategory)
        )
      : listWallets.map(
          ({ address, id, name: title, logo }) =>
            ({
              address,
              id,
              title,
              logo,
              key: title,
              type: StrategyAssetCategoryTypeEnum.SYSTEM,
            } as CreateStrategyAssetCategory)
        )
  ).concat({
    createNewCategory: true,
    id: '',
    title: t(`strategies.addAsset.new.${connectionType}`),
    key: connectionType,
    type: StrategyAssetCategoryTypeEnum.USER,
  });

  const handleBackClick = () => {
    sessionStorage.removeItem('walletStrategyId');
    logger.log('AddAsset:handleBackClick:step', createStrategyAssetStep);

    if (
      createStrategyAssetStep === CreateStrategyAssetStepEnum.CUSTOM_CATEGORY
    ) {
      setCreateStrategyAssetStep(CreateStrategyAssetStepEnum.INITIAL);
    } else {
      setModalTitle(t('strategies.newStrategy.addAsset'));
      setCreateStrategyAssetStep(CreateStrategyAssetStepEnum.INITIAL);
    }
  };

  const dropdownStrategies = useMemo(() => {
    return user?.membership.membershipPlan.features.strategyAssets === -1
      ? [...strategies]
      : [...strategies].filter(
          ({ strategyAssets: { items } }) =>
            items.length <
            (user?.membership.membershipPlan.features.strategyAssets || 1)
        );
  }, [user, strategies]);

  const strategyCurrency =
    allStrategies.find(({ id }) => id === strategyId)?.currency ||
    CurrencyCodeEnum.USD;

  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    initialValues: {
      assetData: {
        id: asset?.id ?? '',
        type: '',
        currency: strategyAsset?.asset?.currency || strategyCurrency,
      },
      assetName: asset?.name ?? '',
      bank: strategyAsset?.bank ?? '',
      categoryId: categories.find(
        ({ key }) => key === assetCategorySelection?.key
      )?.id,
      categoryName: assetCategory?.title ?? '',
      categoryType: assetCategory?.type ?? 'SYSTEM',
      purchasedAt: strategyAsset?.purchasedAt || formatAWSDate(new Date()),
      iconId: strategyAsset?.strategyAssetCategory?.iconId ?? '',
      inspirationSource: strategyAsset?.inspirationSource ?? '',
      loading: false,
      portfolioImport: bankCategories.length + listWallets.length,
      priceAlert: false,
      selectedBankAssets: [],
      selectedWalletAssets: [],
      stopProfitAndLossEntries: strategyAsset?.strategyAssetTargets.items
        ? strategyAsset?.strategyAssetTargets.items.map((t) => ({
            alert: t.alert,
            endDate:
              typeof t.endDate === 'string'
                ? t.endDate
                : formatAWSDate(t.endDate),
            stopLoss: t.stopLoss ?? undefined,
            stopLossPercentage: t.stopLossPercentage ?? undefined,
            stopLossInReportingCurrency:
              t.stopLossInReportingCurrency ?? t.stopLoss ?? undefined,
            stopLossInAssetCurrency: t.stopLossInAssetCurrency ?? undefined,

            stopProfit: t.stopProfit ?? undefined,
            stopProfitPercentage: t.stopProfitPercentage ?? undefined,
            stopProfitInReportingCurrency:
              t.stopProfitInReportingCurrency ?? t.stopProfit ?? undefined,
            stopProfitInAssetCurrency: t.stopProfitInAssetCurrency ?? undefined,
          }))
        : [],
      strategyAssetId: strategyAsset?.id,
      strategyId: strategyId || dropdownStrategies[0]?.id || '',
      strategyName: strategyId
        ? allStrategies.find(({ id }) => id === strategyId)!.name
        : dropdownStrategies[0]?.name ?? '',
      type: 'assetConfiguration',

      quantity: strategyAsset?.unit,
      entryExchangePrice: strategyAsset?.entryExchangePrice ?? 1,
      entryPriceInReportingCurrency:
        strategyAsset?.entryPriceInReportingCurrency ??
        strategyAsset?.entryPrice,
      entryPriceInAssetCurrency: strategyAsset?.entryPriceInAssetCurrency,

      totalInvestmentInAssetCurrency: undefined,
      totalInvestmentInReportingCurrency:
        strategyAsset?.entryPrice && strategyAsset?.unit
          ? strategyAsset?.entryPrice * strategyAsset?.unit
          : undefined,

      // Nft attrs
      collectionName: '',
      collectionData: { collectionId: '', name: '' },
      tokenId: undefined,
      marketplace: '',
      floorPrice: undefined,

      // Property attrs
      propertyEstimationId: '',

      // Custom asset attrs
      currentPriceInReportingCurrency:
        strategyAsset?.currentPriceInReportingCurrency ??
        strategyAsset?.currentPrice,
      currentPriceInAssetCurrency:
        strategyAsset?.currentPriceInAssetCurrency ??
        strategyAsset?.currentPrice,
    },
    onSubmit: () => {},
    validateOnMount: true,
    enableReinitialize: true,
  });

  const loading =
    isContinueButtonLoading ||
    loadingWallets ||
    loadingCategories ||
    createStrategyAssetLoading ||
    updateStrategyAssetLoading ||
    loadingStrategies ||
    loadingCreateCategory ||
    loadingInstitutionsList;

  const reportingCurrency =
    allStrategies.find(({ id }) => id === formik.values.strategyId)?.currency ??
    CurrencyCodeEnum.USD;

  const addAssetToStrategy = ({
    assetId,
    strategyAssetCategoryId,
  }: {
    assetId?: string;
    strategyAssetCategoryId?: string;
  }) => {
    sessionStorage.removeItem('walletStrategyId');

    const input: CreateStrategyAssetInput[] =
      connectionType === 'MANUAL'
        ? prepareManualCreateInput(
            user!.profile!.id!,
            connectionType,
            formik,
            reportingCurrency,
            strategyAssetCategoryId,
            assetId
          )
        : connectionType === 'WALLET'
        ? prepareWalletInput(
            user!.profile!.id!,
            connectionType,
            formik,
            reportingCurrency
          )
        : connectionType === 'BANK'
        ? prepareBankInput(user!.profile!.id!, connectionType, formik)
        : [];

    createStrategyAsset({ variables: { input } })
      .then(() => refetchStrategies())
      .then(() => {
        closeModal();
        onCloseClick();
      })
      .catch((err) => {
        logger.error('AddAsset:createStrategyAsset', err);
        setIsContinueButtonLoading(false);
        closeModal();
        onCloseClick();
      });
  };

  const updateAssetInStrategy = ({
    assetId,
    strategyAssetCategoryId,
  }: {
    assetId?: string;
    strategyAssetCategoryId?: string;
  }) => {
    sessionStorage.removeItem('walletStrategyId');

    const input: UpdateStrategyAssetInput[] =
      connectionType === 'MANUAL'
        ? prepareManualEditInput(
            user!.profile!.id!,
            connectionType,
            formik,
            reportingCurrency,
            strategyAssetCategoryId,
            assetId
          )
        : [];

    updateStrategyAsset({ variables: { input } })
      .then(() => refetchStrategies())
      .then(() => {
        closeModal();
        onCloseClick();
      })
      .catch((err) => {
        logger.error('AddAsset:updateStrategyAsset', err);
        setIsContinueButtonLoading(false);
        closeModal();
        onCloseClick();
      });
  };

  const addPropertyAsset = ({
    propertyEstimationId,
    strategyAssetCategoryId,
    entryPrice,
    purchasedAt,
  }) => {
    addPropertyEstimation({
      variables: {
        id: propertyEstimationId,
        profileId: user?.profile?.id,
        strategyId,
        strategyAssetCategoryId,
        entryPrice,
        purchasedAt,
      },
    })
      .then(() => refetchStrategies())
      .then(() => {
        closeModal();
        onCloseClick();
      })
      .catch((err) => {
        logger.error('AddAsset:addPropertyEstimation', err);
        setIsContinueButtonLoading(false);
        closeModal();
        onCloseClick();
      });
  };

  const createNftToken = ({
    collectionId,
    tokenId,
    entryPrice,
    floorPrice,
    purchasedAt,
    marketplace,
    profileId,
    strategyId,
    strategyAssetCategoryId,
  }) => {
    addNftToken({
      variables: {
        collectionId,
        tokenId,
        entryPrice,
        floorPrice,
        purchasedAt,
        marketplace,
        profileId,
        strategyId,
        strategyAssetCategoryId,
      },
    })
      .then(() => refetchStrategies())
      .then(() => {
        closeModal();
        onCloseClick();
      })
      .catch((err) => {
        logger.error('AddAsset:AddNftToken', err);
        formik.setErrors({
          tokenId: err.message,
        });
        setIsContinueButtonLoading(false);
      });
  };

  const handleContinueClick = () => {
    logger.log('AddAsset:assetCategorySelection', assetCategorySelection);
    logger.log('AddAsset:handleContinueClick:step', createStrategyAssetStep);

    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.INITIAL) {
      const { step, needLoading } = getCreateStrategyAssetStep(
        connectionType,
        assetCategorySelection
      );
      setCreateStrategyAssetStep(step);
      setIsContinueButtonLoading(needLoading);
    } else {
      setIsContinueButtonLoading(true);

      if (createStrategyAssetStep === CreateStrategyAssetStepEnum.NFT) {
        logger.log('AddAsset:continue:createNftToken', formik.values);
        const {
          collectionData,
          tokenId,
          purchasedAt,
          categoryId,
          entryPriceInAssetCurrency,
          marketplace,
          floorPrice,
        } = formik.values;

        createNftToken({
          strategyId,
          strategyAssetCategoryId: categoryId,
          marketplace,
          profileId: user?.profile?.id,
          purchasedAt,
          entryPrice: entryPriceInAssetCurrency,
          floorPrice,
          tokenId,
          collectionId: collectionData.collectionId,
        });
      } else if (
        createStrategyAssetStep === CreateStrategyAssetStepEnum.PROPERTY
      ) {
        logger.log('AddAsset:continue:addPropertyAsset', formik.values);

        const {
          propertyEstimationId,
          categoryId,
          entryPriceInReportingCurrency,
          purchasedAt,
        } = formik.values;

        addPropertyAsset({
          propertyEstimationId,
          strategyAssetCategoryId: categoryId,
          entryPrice: entryPriceInReportingCurrency,
          purchasedAt,
        });
      } else if (
        createStrategyAssetStep === CreateStrategyAssetStepEnum.CUSTOM_CATEGORY
      ) {
        logger.log('AddAsset:continue:createStrategyAssetCategory');

        createStrategyAssetCategory({
          variables: {
            input: {
              profileId: user?.profile?.id,
              iconId: formik.values?.iconId,
              title: formik.values?.categoryName,
            },
          },
        })
          .then(
            ({
              data,
            }: FetchResult<{
              createStrategyAssetCategory: CreateStrategyAssetCategory;
            }>) => {
              logger.log(
                'AddAsset:continue:createStrategyAssetCategory:res',
                data
              );

              setCreateStrategyAssetStep(
                CreateStrategyAssetStepEnum.CUSTOM_ASSET
              );
              setAssetCategorySelection(data?.createStrategyAssetCategory);
              setIsContinueButtonLoading(false);
            }
          )
          .catch((err) => {
            logger.error('AddAsset:createStrategyAssetCategory', err);
            setIsContinueButtonLoading(false);
          });
      } else {
        logger.log('AddAsset:continue:addAssetToStrategy');

        logger.log(
          'AddAsset:continue:addAssetToStrategy:cat',
          assetCategorySelection?.id,
          formik.values.categoryId,
          assetCategory?.title
        );

        if (action === 'EDIT') {
          updateAssetInStrategy({
            strategyAssetCategoryId: assetCategorySelection?.id,
            assetId: formik.values?.assetData?.id,
          });
        } else {
          addAssetToStrategy({
            strategyAssetCategoryId: assetCategorySelection?.id,
            assetId: formik.values?.assetData?.id,
          });
        }
      }
    }
  };

  const isDisabledSaveButton = useMemo(() => {
    if (loading) {
      return true;
    }

    // No strategy was selected on add asset from market/detail
    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.FINANCIAL) {
      return (
        !formik.values.strategyId ||
        formik.values.assetName.length < 2 ||
        !formik.values.quantity ||
        !formik.values.entryPriceInReportingCurrency
      );
    }

    // Custom category name less than 2
    if (
      createStrategyAssetStep === CreateStrategyAssetStepEnum.CUSTOM_CATEGORY
    ) {
      return formik.values.categoryName.length < 2;
    }

    // Require subscription upgrade to add more wallet or bank
    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.INITIAL) {
      return (
        !user ||
        (connectionType !== ConnectionTypeEnum.MANUAL &&
          user.membership.membershipPlan.sku ===
            MembershipPlanSkuEnum.Camelot) ||
        (user.membership.membershipPlan.features.portfolioImport !== -1 &&
          user.membership.membershipPlan.features.portfolioImport <=
            formik.values.portfolioImport)
      );
    }

    // No asset was selected from bank account
    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.BANK_ASSETS) {
      return (
        connectionType === 'BANK' &&
        formik.values.selectedBankAssets.length === 0
      );
    }

    // No entryPrice was set on custom asset
    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.CUSTOM_ASSET) {
      return !formik.values.entryPriceInReportingCurrency;
    }

    // No entryPrice/purchasedAt was set on property asset
    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.PROPERTY) {
      return (
        !formik.values.propertyEstimationId ||
        !formik.values.entryPriceInAssetCurrency ||
        !formik.values.purchasedAt
      );
    }

    // No entryPrice/purchasedAt/tokenId was set on NFT asset
    if (createStrategyAssetStep === CreateStrategyAssetStepEnum.NFT) {
      return (
        !formik.values.collectionData?.collectionId ||
        !formik.values.tokenId ||
        !formik.values.entryPriceInAssetCurrency ||
        !formik.values.floorPrice
      );
    }

    return false;
  }, [
    formik.values,
    connectionType,
    createStrategyAssetStep,
    strategyId,
    user,
    loading,
  ]);

  useEffect(() => {
    logger.log(
      'AddAsset:useEffect:strategyAssetCategories',
      connectionType,
      strategyAssetCategories
    );
    setAssetCategorySelection(
      assetCategory
        ? categories.find(({ key }) => key === assetCategory?.key)
        : categories[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionType, strategyAssetCategories.length]);

  useEffect(() => {
    if (walletAccountRef.current) {
      setCreateStrategyAssetStep(CreateStrategyAssetStepEnum.WALLET_ASSETS);
    }

    return () => {
      walletAccountRef.current = undefined;
    };
  }, []);

  const onPlaidCompleted = ({ setAccessToken: itemId }) => {
    refetchBankCategories().then(({ data: { getInstitutionsList } }) => {
      const { id, bankName: title } = getInstitutionsList.find(
        ({ id }) => id === itemId
      )!;
      setAssetCategorySelection({
        id,
        title,
        key: title,
        type: StrategyAssetCategoryTypeEnum.SYSTEM,
      });
      setCreateStrategyAssetStep(CreateStrategyAssetStepEnum.BANK_ASSETS);
      getNewPlaidLinkToken().then(() => {
        logger.log('AddAsset:onPlaidCompleted:getNewPlaidLinkToken');
      });
    });
  };

  const onPlaidExit = () => {
    setCreateStrategyAssetStep(CreateStrategyAssetStepEnum.INITIAL);
    getNewPlaidLinkToken().then(() => {
      logger.log('AddAsset:onPlaidExit:getNewPlaidLinkToken');
    });
  };

  const onWalletConnectCompleted = (data: {
    walletConnect: { connectUrl: string };
  }) => {
    logger.log('AddAsset:onWalletConnectCompleted', data);
    window.open(data?.walletConnect?.connectUrl, '_self');
  };

  const onWalletConnectError = (err: Error) => {
    logger.error('AddAsset:onWalletConnectExit', err);
    setIsContinueButtonLoading(false);
    setCreateStrategyAssetStep(CreateStrategyAssetStepEnum.INITIAL);
  };

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col h-full flex-grow-1 overflow-y-scroll scrollbar-hide">
        <Debug>
          <>{`STEP: ${createStrategyAssetStep} - ${connectionType} - ${strategyAsset?.id} - ${assetCategory?.key}`}</>
        </Debug>

        {/* BANK CONNECT */}
        {createStrategyAssetStep ===
          CreateStrategyAssetStepEnum.BANK_CONNECT && (
          <BankConnect
            token={token}
            onCompleted={onPlaidCompleted}
            onExit={onPlaidExit}
          />
        )}

        {/* WALLET CONNECT */}
        {createStrategyAssetStep ===
          CreateStrategyAssetStepEnum.WALLET_CONNECT && (
          <WalletConnect
            strategyId={strategyId}
            onCompleted={onWalletConnectCompleted}
            onError={onWalletConnectError}
          />
        )}

        {loading ? (
          <div className="flex h-full w-full">
            <Loading className="m-auto" status />
          </div>
        ) : (
          <>
            {/* INITIAL */}
            {createStrategyAssetStep ===
              CreateStrategyAssetStepEnum.INITIAL && (
              <ChooseConnectionAndAssetCategory
                assetCategorySelection={assetCategorySelection}
                categories={categories}
                connectionType={connectionType}
                formik={formik}
                loading={loadingCategories}
                onAssetCategorySelectionChange={setAssetCategorySelection}
                setConnectionType={setConnectionType}
                skipStrategy={skipStrategy}
              />
            )}

            {/* BANK ASSET */}
            {createStrategyAssetStep ===
              CreateStrategyAssetStepEnum.BANK_ASSETS && (
              <BankAssets formik={formik} itemId={assetCategorySelection!.id} />
            )}

            {/* WALLET ASSET */}
            {createStrategyAssetStep ===
              CreateStrategyAssetStepEnum.WALLET_ASSETS && (
              <WalletAssets
                walletAccount={walletAccountRef.current}
                formik={formik}
                itemId={assetCategorySelection!.id}
              />
            )}

            {/* FINANCIAL ASSET */}
            {createStrategyAssetStep ===
              CreateStrategyAssetStepEnum.FINANCIAL && (
              <FinancialAssetForm
                strategyAsset={strategyAsset}
                isNewStrategy={false}
                assetCategory={assetCategorySelection!}
                reportingCurrency={reportingCurrency}
                formik={formik}
                strategyId={strategyId}
                isTooltipIconVisible={isTooltipIconVisible}
                action={action}
              />
            )}

            {/* CUSTOM ASSET */}
            {(createStrategyAssetStep ===
              CreateStrategyAssetStepEnum.CUSTOM_CATEGORY ||
              createStrategyAssetStep ===
                CreateStrategyAssetStepEnum.CUSTOM_ASSET) && (
              <AddCustomAssetOrCreateStrategyAssetCategory
                reportingCurrency={reportingCurrency}
                step={createStrategyAssetStep}
                formik={formik}
                strategyAssetCategories={strategyAssetCategories}
                action={action}
              />
            )}

            {/* PROPERTY ASSET */}
            {createStrategyAssetStep ===
              CreateStrategyAssetStepEnum.PROPERTY && (
              <PropertyAssetForm
                formik={formik}
                reportingCurrency={reportingCurrency}
              />
            )}

            {/* NFT ASSET */}
            {createStrategyAssetStep === CreateStrategyAssetStepEnum.NFT && (
              <NFTAssetForm
                formik={formik}
                reportingCurrency={reportingCurrency}
              />
            )}
          </>
        )}

        <FooterNavigator
          buttonsPosition={
            createStrategyAssetStep === CreateStrategyAssetStepEnum.INITIAL ||
            action === 'EDIT' ||
            action === 'ADD_TO_STRATEGY'
              ? 'end'
              : 'between'
          }
          cancelButtonName="Back"
          className={`
            border-t
            mt-auto
            left-0
            right-0
            bottom-0
            rounded-b-xl
            border-grey-10
            bg-white
            flex-grow-0
            flex-shrink-0
            dark:bg-grey-90
            dark:border-grey-80
          `}
          cancelButtonClassName="flex items-center gap-2 border-none"
          cancelButtonSVG={<Icon svgName="arrowBack" canUseDarkMode={false} />}
          loading={loading}
          oneButton={
            createStrategyAssetStep === CreateStrategyAssetStepEnum.INITIAL ||
            action === 'EDIT' ||
            action === 'ADD_TO_STRATEGY'
          }
          onSave={handleContinueClick}
          onCancel={handleBackClick}
          saveButtonName={
            createStrategyAssetStep === CreateStrategyAssetStepEnum.INITIAL
              ? 'continue'
              : 'save'
          }
          isDisabledSaveButton={isDisabledSaveButton}
          isDisabledCancelButton={loading}
        />
      </div>

      {/* Show upgrade modal if user try to add a price alert and his membership not include it */}
      <UpgradeSubscription
        isOpen={
          !user?.membership?.membershipPlan?.features?.smartAlert &&
          formik.values.priceAlert
        }
        onCloseClick={() => {
          formik.setValues({ ...formik.values, priceAlert: false });
        }}
        onUpgradeClick={() => {
          onCloseClick?.();
          skipStrategy?.();
        }}
        feature={'SMART_ALERT'}
        viewMode={'modal'}
      />
      <Debug
        data={formik.values}
        style={{
          height: '100%',
          overflow: 'scroll',
          position: 'absolute',
          width: '300px',
          top: 0,
          right: '-300px',
        }}
      />
    </FormikProvider>
  );
};
